import { UserRestrictions } from './UserRestrictions';

import type { CustomerSummaryWebApiDto } from '@zg-rentals/rental-manager-env';
import type { CometUserRoleType } from '@zg-rentals/ts-rental-manager-api-client';

const UserSummary = ({
  activeListingCount,
  totalListingCount,
  isPartialResponse,
}: {
  activeListingCount?: number;
  totalListingCount?: number;
  isPartialResponse?: boolean;
} = {}) => ({
  activeListingCount: activeListingCount === undefined ? undefined : Number(activeListingCount),
  totalListingCount: totalListingCount === undefined ? undefined : Number(totalListingCount),
  isPartialResponse: Boolean(isPartialResponse),
});

export const UserFeatures = ({
  canAccessAnalytics = false,
  canAccessBilling = false,
  canAccessBusinessVerificationLite = false,
  canAccessConnectedExperience = false,
  canAccessContacts = false,
  canAccessFeatureLaunchNotifications = false,
  canAccessFeedClaimAndConvert = false,
  canAccessInvoicePayments = false,
  canAccessListingCriteria = false,
  canAccessMainAccountSettings = false,
  canAccessMainAnalytics = false,
  canAccessMainBilling = false,
  canAccessMainContacts = false,
  canAccessMainContactSupport = false,
  canAccessMainHelp = false,
  canAccessMainLeadManagement = false,
  canAccessMainMaintenanceTracker = false,
  canAccessMainMessages = false,
  canAccessMainMyProperties = false,
  canAccessMainNotificationCenter = false,
  canAccessMainPayments = false,
  canAccessMainResources = false,
  canAccessMainSecuritySettings = false,
  canAccessMaintenanceTracker = false,
  canAccessMessages = false,
  canAccessModularListing = false,
  canAccessMultiFamilyResources = false,
  canAccessNewPhotoUpload = false,
  canAccessPostActivationFeedListingEdits = false,
  canAccessPostActivationListingEdits = false,
  canAccessUserManagement = false,
  canAccessUserManagementApplicationPermission = false,
  canAddListing = false,
  canClaimFreeListings = false,
  canDeleteListing = false,
  canRespondToReview = false,
  canSwitchListingSource = false,
  canUpdateListing = false,
  canViewClaimFeedListing = false,
  canViewContactEmailDeliverability = false,
  canViewOnboardingFlow = false,
  canViewSingleFamilyFeedListingDetails = false,
} = {}) => ({
  canAccessAnalytics: Boolean(canAccessAnalytics),
  canAccessBilling: Boolean(canAccessBilling),
  canAccessBusinessVerificationLite: Boolean(canAccessBusinessVerificationLite),
  canAccessConnectedExperience: Boolean(canAccessConnectedExperience),
  canAccessContacts: Boolean(canAccessContacts),
  canAccessFeatureLaunchNotifications: Boolean(canAccessFeatureLaunchNotifications),
  canAccessFeedClaimAndConvert: Boolean(canAccessFeedClaimAndConvert),
  canAccessInvoicePayments: Boolean(canAccessInvoicePayments),
  canAccessListingCriteria: Boolean(canAccessListingCriteria),
  canAccessMainAccountSettings: Boolean(canAccessMainAccountSettings),
  canAccessMainAnalytics: Boolean(canAccessMainAnalytics),
  canAccessMainBilling: Boolean(canAccessMainBilling),
  canAccessMainContacts: Boolean(canAccessMainContacts),
  canAccessMainContactSupport: Boolean(canAccessMainContactSupport),
  canAccessMainHelp: Boolean(canAccessMainHelp),
  canAccessMainLeadManagement: Boolean(canAccessMainLeadManagement),
  canAccessMainMaintenanceTracker: Boolean(canAccessMainMaintenanceTracker),
  canAccessMainMessages: Boolean(canAccessMainMessages),
  canAccessMainMyProperties: Boolean(canAccessMainMyProperties),
  canAccessMainNotificationCenter: Boolean(canAccessMainNotificationCenter),
  canAccessMainPayments: Boolean(canAccessMainPayments),
  canAccessMainResources: Boolean(canAccessMainResources),
  canAccessMainSecuritySettings: Boolean(canAccessMainSecuritySettings),
  canAccessMaintenanceTracker: Boolean(canAccessMaintenanceTracker),
  canAccessMessages: Boolean(canAccessMessages),
  canAccessModularListing: Boolean(canAccessModularListing),
  canAccessMultiFamilyResources: Boolean(canAccessMultiFamilyResources),
  canAccessNewPhotoUpload: Boolean(canAccessNewPhotoUpload),
  canAccessPostActivationFeedListingEdits: Boolean(canAccessPostActivationFeedListingEdits),
  canAccessPostActivationListingEdits: Boolean(canAccessPostActivationListingEdits),
  canAccessUserManagement: Boolean(canAccessUserManagement),
  canAccessUserManagementApplicationPermission: Boolean(canAccessUserManagementApplicationPermission),
  canAddListing: Boolean(canAddListing),
  canClaimFreeListings: Boolean(canClaimFreeListings),
  canDeleteListing: Boolean(canDeleteListing),
  canRespondToReview: Boolean(canRespondToReview),
  canSwitchListingSource: Boolean(canSwitchListingSource),
  canUpdateListing: Boolean(canUpdateListing),
  canViewClaimFeedListing: Boolean(canViewClaimFeedListing),
  canViewContactEmailDeliverability: Boolean(canViewContactEmailDeliverability),
  canViewOnboardingFlow: Boolean(canViewOnboardingFlow),
  canViewSingleFamilyFeedListingDetails: Boolean(canViewSingleFamilyFeedListingDetails),
});

const User = ({
  apiResponseServerSideRequestCookies = [] as Array<string>,
  applicationsEnabled = false,
  customers = [] as Array<CustomerSummaryWebApiDto>,
  email = '',
  encodedGuidZuid = '', // deprecated for `encodedZuid`
  encodedZuid = '',
  features = {},
  firstName = '',
  hasPaidForListing = false,
  hasSeenNewPaymentsPage = false,
  hasSeenPaymentsAggregatedViewModal = false,
  hasStrongAuthentication = false,
  impersonatorEmail = '',
  impersonatorUserToken = '',
  isEmailVerified = false,
  isEnterpriseCustomer = false,
  isIdVerified = false,
  isMocked = false,
  isMultifamilyCustomer = false,
  isPostingPathAdmin = false,
  isPropertyManager = false,
  isPropertyManagerCompanyAdmin = false,
  isRocketshipCustomer = false,
  isSelfServeCustomer = false,
  isSingleFamilyEnterpriseCustomer = false,
  isTwoFactorAuthEnabled = false,
  isTwoFactorAuthenticated = false,
  isUserBlocked = false,
  lastName = '',
  loggedIn = false,
  originalServerSideRequestCookies = {},
  paymentsAcceptedCount = 0,
  paymentsAccessAllowed = false,
  phoneNumber = '',
  profileImageUrl = '',
  restrictions = {
    isAllowed: true,
  },
  roles = [] as Array<string>,
  roleTypes = [] as Array<CometUserRoleType>,
  sessionSecondsRemaining = 1800,
  summary = UserSummary(),
  userManagementCompanyId = '',
  userName = '',
  userToken = '',
} = {}) => ({
  apiResponseServerSideRequestCookies,
  applicationsEnabled: Boolean(applicationsEnabled),
  customers,
  email: String(email),
  encodedGuidZuid: String(encodedGuidZuid),
  encodedZuid: String(encodedZuid),
  features: UserFeatures(features),
  firstName: String(firstName),
  hasSeenNewPaymentsPage: Boolean(hasSeenNewPaymentsPage),
  hasPaidForListing: Boolean(hasPaidForListing),
  hasSeenPaymentsAggregatedViewModal: Boolean(hasSeenPaymentsAggregatedViewModal),
  hasStrongAuthentication: Boolean(hasStrongAuthentication),
  impersonatorEmail: String(impersonatorEmail),
  impersonatorUserToken: String(impersonatorUserToken),
  isEmailVerified: Boolean(isEmailVerified),
  isEnterpriseCustomer: Boolean(isEnterpriseCustomer),
  isIdVerified: Boolean(isIdVerified),
  isMocked: Boolean(isMocked),
  isMultifamilyCustomer: Boolean(isMultifamilyCustomer),
  isPostingPathAdmin: Boolean(isPostingPathAdmin),
  isPropertyManager: Boolean(isPropertyManager),
  isPropertyManagerCompanyAdmin: Boolean(isPropertyManagerCompanyAdmin),
  isRocketshipCustomer: Boolean(isRocketshipCustomer),
  isSelfServeCustomer: Boolean(isSelfServeCustomer),
  isSingleFamilyEnterpriseCustomer: Boolean(isSingleFamilyEnterpriseCustomer),
  isTwoFactorAuthEnabled: Boolean(isTwoFactorAuthEnabled),
  isTwoFactorAuthenticated: Boolean(isTwoFactorAuthenticated),
  isUserBlocked: Boolean(isUserBlocked),
  lastName: String(lastName),
  loggedIn: Boolean(loggedIn),
  originalServerSideRequestCookies,
  paymentsAcceptedCount: Number(paymentsAcceptedCount),
  paymentsAccessAllowed: Boolean(paymentsAccessAllowed),
  phoneNumber: String(phoneNumber),
  profileImageUrl: String(profileImageUrl),
  restrictions: UserRestrictions(restrictions),
  roles: roles.map((role) => String(role)),
  roleTypes: roleTypes as Array<CometUserRoleType>,
  sessionSecondsRemaining: Number(sessionSecondsRemaining),
  summary: UserSummary(summary),
  userManagementCompanyId: String(userManagementCompanyId),
  userName: String(userName),
  userToken: String(userToken),
});

export default User;

export type UserProps = ReturnType<typeof User>;
